<template>
  <div v-if="slides.length" class="hero-slider" :style="{ '--autoplay': autoplayMs + 'ms' }">
    <!-- Slajdy -->
    <div class="hero-track" :style="trackStyle">
      <div
        v-for="(slide, idx) in slides"
        :key="slide.id"
        class="hero-slide"
      >
        <!-- Obraz (mobilny jeśli jest i viewport < 768px) -->
        <picture>
          <source
            v-if="slide.image_mobile_url"
            :srcset="slide.image_mobile_url"
            media="(max-width: 767px)"
          />
          <img
            :src="slide.image_url"
            :alt="slide.title || 'Banner'"
            class="hero-img"
            :loading="idx === 0 ? 'eager' : 'lazy'"
          />
        </picture>

        <!-- Overlay przyciemnienie -->
        <div
          class="hero-overlay"
          :style="{ background: `rgba(0,0,0,${slide.overlay_opacity})` }"
        />

        <!-- Treść -->
        <div class="hero-content" :style="{ color: slide.text_color }">
          <h2 v-if="slide.title" class="hero-title">{{ slide.title }}</h2>
          <p v-if="slide.subtitle" class="hero-subtitle">{{ slide.subtitle }}</p>
          <a
            v-if="slide.cta_label && slide.cta_url"
            :href="slide.cta_url"
            class="hero-cta"
            :style="{ borderColor: slide.text_color, color: slide.text_color }"
          >
            {{ slide.cta_label }}
          </a>
        </div>
      </div>
    </div>

    <!-- Strzałki (tylko gdy > 1 slajd) -->
    <template v-if="slides.length > 1">
      <button class="hero-arrow hero-arrow--left" @click="prev" aria-label="Poprzedni slajd">
        &#8249;
      </button>
      <button class="hero-arrow hero-arrow--right" @click="next" aria-label="Następny slajd">
        &#8250;
      </button>

      <!-- Dots -->
      <div class="hero-dots">
        <button
          v-for="(_, idx) in slides"
          :key="idx"
          class="hero-dot"
          :class="{ active: idx === current }"
          @click="goTo(idx)"
          :aria-label="`Slajd ${idx + 1}`"
        />
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "AppHeroSlider",
  props: {
    /** slug bannera do pobrania z API */
    bannerSlug: {
      type: String,
      default: "main",
    },
  },
  data() {
    return {
      slides: [],
      autoplayMs: 5000,
      current: 0,
      timer: null,
      touchStartX: 0,
    };
  },
  computed: {
    trackStyle() {
      return { transform: `translateX(-${this.current * 100}%)` };
    },
  },
  async mounted() {
    await this.loadBanner();
    this.startAutoplay();
    this.$el.addEventListener("touchstart", this.onTouchStart, { passive: true });
    this.$el.addEventListener("touchend", this.onTouchEnd, { passive: true });
  },
  beforeUnmount() {
    this.stopAutoplay();
    this.$el.removeEventListener("touchstart", this.onTouchStart);
    this.$el.removeEventListener("touchend", this.onTouchEnd);
  },
  methods: {
    async loadBanner() {
      try {
        const res = await fetch(`/api/v1/banners/${this.bannerSlug}/`);
        if (!res.ok) return;
        const data = await res.json();
        this.slides = data.slides || [];
        this.autoplayMs = data.autoplay_ms || 5000;
      } catch (e) {
        console.warn("HeroSlider: nie udało się załadować bannera", e);
      }
    },
    goTo(idx) {
      this.current = idx;
      this.restartAutoplay();
    },
    next() {
      this.current = (this.current + 1) % this.slides.length;
      this.restartAutoplay();
    },
    prev() {
      this.current = (this.current - 1 + this.slides.length) % this.slides.length;
      this.restartAutoplay();
    },
    startAutoplay() {
      if (!this.autoplayMs || this.slides.length < 2) return;
      this.timer = setInterval(this.next, this.autoplayMs);
    },
    stopAutoplay() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    },
    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    },
    onTouchStart(e) { this.touchStartX = e.changedTouches[0].screenX; },
    onTouchEnd(e) {
      const diff = this.touchStartX - e.changedTouches[0].screenX;
      if (diff > 50) this.next();
      else if (diff < -50) this.prev();
    },
  },
};
</script>

<style scoped>
.hero-slider {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #111;
  /* wysokość responsywna: 55vw, min 280px, max 640px */
  height: clamp(280px, 48vw, 640px);
}

.hero-track {
  display: flex;
  height: 100%;
  transition: transform 0.55s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.hero-slide {
  position: relative;
  flex: 0 0 100%;
  height: 100%;
  overflow: hidden;
}

.hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.hero-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px 16px;
  gap: 12px;
}

.hero-title {
  margin: 0;
  font-size: clamp(1.4rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  text-shadow: 0 2px 12px rgba(0,0,0,0.35);
}

.hero-subtitle {
  margin: 0;
  font-size: clamp(0.95rem, 2vw, 1.3rem);
  max-width: 640px;
  opacity: 0.92;
  text-shadow: 0 1px 6px rgba(0,0,0,0.25);
}

.hero-cta {
  display: inline-block;
  margin-top: 8px;
  padding: 12px 32px;
  border: 2px solid currentColor;
  border-radius: 9999px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  transition: background 0.2s, color 0.2s;
  backdrop-filter: blur(4px);
  background: rgba(255,255,255,0.1);
}
.hero-cta:hover {
  background: rgba(255,255,255,0.25);
}

/* Strzałki */
.hero-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255,255,255,0.18);
  backdrop-filter: blur(6px);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  font-size: 28px;
  line-height: 1;
  color: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.2s;
  z-index: 10;
}
.hero-arrow:hover { background: rgba(255,255,255,0.35); }
.hero-arrow--left  { left: 14px; }
.hero-arrow--right { right: 14px; }

/* Dots */
.hero-dots {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}
.hero-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.7);
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s, transform 0.2s;
}
.hero-dot.active {
  background: #fff;
  transform: scale(1.25);
}

@media (max-width: 640px) {
  .hero-arrow { width: 36px; height: 36px; font-size: 22px; }
  .hero-arrow--left  { left: 8px; }
  .hero-arrow--right { right: 8px; }
}
</style>
